import React, { useEffect, useRef, useState, useCallback } from "react";
import { applyNodes, parseCubeLUT } from "./gradeMath";
import { WebGPUGradeEngine, isWebGPUAvailable } from "./webgpuEngine";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolve = (u) => (u && !u.startsWith("http") ? `${BACKEND}${u}` : u);

// module-level cache: lut url → parsed { size, data }
const lutCache = {};

async function ensureLut(url) {
  if (!url || lutCache[url]) return;
  try {
    const res = await fetch(resolve(url));
    lutCache[url] = parseCubeLUT(await res.text());
  } catch (e) { lutCache[url] = null; }
}

/**
 * Workbench preview — dual engine:
 *   • WebGPU (Chrome/Edge): 60fps WGSL path — cdl/adjust/curves/qualifier/wipe
 *   • CPU canvas2d fallback: Safari, matte view, or nodes with a 3D LUT
 * Both implement the same gradeMath parity contract. Scopes always read from
 * a small CPU mini-render so they work identically on either engine.
 */
export const PreviewCanvas = ({ frames, heroUrl, nodes, bypass, matteIdx = -1, onPixels, onRegisterCapture }) => {
  const gpuCanvasRef = useRef(null);
  const cpuCanvasRef = useRef(null);
  const miniRef = useRef(null);     // offscreen for scopes
  const srcRef = useRef(null);      // offscreen original full-res
  const bitmapRef = useRef(null);
  const engineRef = useRef(null);
  const [frameIdx, setFrameIdx] = useState(-1);
  const [wipe, setWipe] = useState(1);
  // which engine ACTUALLY rendered the last frame — drives canvas visibility
  // (intent alone is wrong: gpu may be present but the adapter can fail).
  const [activeEngine, setActiveEngine] = useState("cpu");
  const raf = useRef(null);

  const currentUrl = frameIdx < 0 ? heroUrl : frames[frameIdx];
  const hasLut = (nodes || []).some((n) => n.enabled !== false && n.ops?.lut_ref?.url);
  const wantGpu = isWebGPUAvailable() && !hasLut && matteIdx < 0 && !bypass;

  // load source frame
  useEffect(() => {
    if (!currentUrl) return;
    let cancelled = false;
    (async () => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = resolve(currentUrl);
      await img.decode().catch(() => {});
      if (cancelled || !img.naturalWidth) return;
      const off = document.createElement("canvas");
      off.width = img.naturalWidth; off.height = img.naturalHeight;
      off.getContext("2d").drawImage(img, 0, 0);
      srcRef.current = off;
      bitmapRef.current = await createImageBitmap(img);
      if (engineRef.current?.ready) engineRef.current.setSource(bitmapRef.current);
      draw();
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUrl]);

  // fetch+parse any LUTs referenced by nodes
  useEffect(() => {
    (nodes || []).forEach((n) => { if (n.ops?.lut_ref?.url) ensureLut(n.ops.lut_ref.url).then(draw); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes]);

  // init WebGPU once
  useEffect(() => {
    if (!isWebGPUAvailable() || engineRef.current) return;
    const eng = new WebGPUGradeEngine();
    eng.init(gpuCanvasRef.current).then(() => {
      engineRef.current = eng;
      if (bitmapRef.current) eng.setSource(bitmapRef.current);
      draw();
    }).catch(() => { engineRef.current = null; });
    return () => { try { eng.destroy(); } catch (e) { /* noop */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const drawScopes = useCallback(() => {
    if (!onPixels || !srcRef.current) return;
    const src = srcRef.current;
    const w = 240, h = Math.max(2, Math.round((src.height / src.width) * 240));
    if (!miniRef.current) miniRef.current = document.createElement("canvas");
    const mini = miniRef.current;
    mini.width = w; mini.height = h;
    const mctx = mini.getContext("2d", { willReadFrequently: true });
    mctx.drawImage(src, 0, 0, w, h);
    try {
      const id = mctx.getImageData(0, 0, w, h);
      if (!bypass) applyNodes(id, nodes, { matteIdx, lutCache });
      onPixels(id);
    } catch (e) { /* tainted canvas */ }
  }, [nodes, bypass, matteIdx, onPixels]);

  const draw = useCallback(() => {
    if (raf.current) cancelAnimationFrame(raf.current);
    raf.current = requestAnimationFrame(() => {
      const src = srcRef.current;
      if (!src) return;
      if (wantGpu && engineRef.current?.ready) {
        setActiveEngine("gpu");
        engineRef.current.render(nodes, wipe);
      } else {
        setActiveEngine("cpu");
        const canvas = cpuCanvasRef.current;
        canvas.width = src.width; canvas.height = src.height;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        ctx.drawImage(src, 0, 0);
        if (!bypass) {
          try {
            const id = ctx.getImageData(0, 0, src.width, src.height);
            applyNodes(id, nodes, { matteIdx, lutCache });
            ctx.putImageData(id, 0, 0);
            if (wipe < 1 && matteIdx < 0) {
              const w = Math.round(src.width * wipe);
              ctx.drawImage(src, w, 0, src.width - w, src.height, w, 0, src.width - w, src.height);
              ctx.fillStyle = "#f4861f";
              ctx.fillRect(w - 1, 0, 2, src.height);
            }
          } catch (e) { /* tainted canvas */ }
        }
      }
      drawScopes();
    });
  }, [nodes, bypass, wipe, matteIdx, wantGpu, drawScopes]);

  useEffect(() => { draw(); }, [draw]);

  // Stills capture — always a deterministic CPU render of the CURRENT grade
  // at full proxy resolution, regardless of which engine drives the screen.
  useEffect(() => {
    if (!onRegisterCapture) return;
    onRegisterCapture(async () => {
      const src = srcRef.current;
      if (!src) return null;
      const out = document.createElement("canvas");
      out.width = src.width; out.height = src.height;
      const ctx = out.getContext("2d", { willReadFrequently: true });
      ctx.drawImage(src, 0, 0);
      if (!bypass) {
        try {
          const id = ctx.getImageData(0, 0, src.width, src.height);
          applyNodes(id, nodes, { lutCache });
          ctx.putImageData(id, 0, 0);
        } catch (e) { /* tainted canvas */ }
      }
      return await new Promise((res) => out.toBlob(res, "image/jpeg", 0.92));
    });
  }, [nodes, bypass, onRegisterCapture]);

  return (
    <div className="flex flex-col gap-2" data-testid="grade-preview">
      <div className="relative bg-black rounded-xl overflow-hidden border border-white/10">
        <canvas ref={gpuCanvasRef} className="w-full block"
          style={{ display: activeEngine === "gpu" ? "block" : "none" }}
          data-testid="grade-preview-canvas-gpu" />
        <canvas ref={cpuCanvasRef} className="w-full block"
          style={{ display: activeEngine === "gpu" ? "none" : "block" }}
          data-testid="grade-preview-canvas" />
        <span className="absolute top-2 right-2 px-1.5 py-0.5 rounded bg-black/70 font-mono text-[9px] text-white/50"
          data-testid="grade-engine-badge">{activeEngine === "gpu" ? "WebGPU" : "CPU"}</span>
        {matteIdx >= 0 && (
          <span className="absolute top-2 left-2 px-1.5 py-0.5 rounded bg-orange-500/90 text-black font-mono text-[9px] font-bold">MATTE VIEW</span>
        )}
      </div>
      <div className="flex items-center gap-3 text-xs text-white/50">
        <span className="uppercase tracking-wider font-mono">Wipe</span>
        <input type="range" min="0" max="1" step="0.01" value={wipe}
          onChange={(e) => setWipe(parseFloat(e.target.value))}
          className="flex-1 accent-orange-500" data-testid="grade-wipe-slider" />
      </div>
      {frames?.length > 0 && (
        <div className="flex items-center gap-3 text-xs text-white/50">
          <span className="uppercase tracking-wider font-mono">Scrub</span>
          <input type="range" min="-1" max={frames.length - 1} step="1"
            value={frameIdx}
            onChange={(e) => setFrameIdx(parseInt(e.target.value, 10))}
            className="flex-1 accent-orange-500" data-testid="grade-scrub-slider" />
          <span className="font-mono">{frameIdx < 0 ? "HERO" : `F${frameIdx + 1}/${frames.length}`}</span>
        </div>
      )}
    </div>
  );
};
